import React, { useCallback, useMemo, useState } from "react"
import Split from "react-split"
import { Button, Dropdown, Modal, Radio, Select, Switch, Tooltip } from "antd"
import type { RadioChangeEvent } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleInfo,
  faFileExcel,
  faFloppyDisk,
  faPlay,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import Output from "../Output.component"
import {
  BuilderDropZone,
  BuilderStatsRow,
  BuilderOperationToggle,
  BuilderUniverseSelector,
  BuilderQueryPreview,
} from "shared/ui/Builder"
import BuilderConditions from "./BuilderConditions.component"
import BuilderTabs from "./BuilderTabs.component"
import ConsoleTableStyleButton from "../ConsoleTableStyleButton.component"
import SignificanceControl from "shared/ui/Significance/SignificanceControl.component"
import {
  useQueryBuilderStore,
  useBuilderScript,
  useActiveBuilder,
  useActiveBuilderResult,
  useBatchRunResult,
} from "stores/queryBuilderStore"
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import {
  SIGNIFICANCE_TESTED_STATISTICS,
  SignificanceSettings,
} from "types/significance.types"

interface QueryBuilderProps {
  /** Engine-level "running query" flag — global to the container. */
  scriptInProgress: boolean
  asyncScriptInProgress: boolean
  splitSizes: number[]
  onSplitChange: (sizes: number[]) => void
  direction?: "vertical" | "horizontal"
  order?: "console-first" | "output-first"
  /**
   * Save the active builder to the backend under the given name. When the
   * name differs from the builder's current display name, the builder is
   * also renamed in-store. Resolves when the backend round-trip completes.
   */
  onSaveActiveBuilder?: (name: string) => Promise<void>
  /**
   * Delete the active builder. The container handles backend cleanup (drops
   * the saved record if any) plus the local store removal — order matters
   * because the saved name has to be read before the local entry goes.
   */
  onDeleteActiveBuilder?: () => Promise<void>
  /**
   * Run every runnable Builder in one engine query (single RESET, each
   * Transform pipeline emitted once). The result lands in `batchRunResult`
   * via `setBatchResult` and surfaces in the Output pane.
   */
  onRunBatch?: () => Promise<void>
  /**
   * Build an .xlsx from the most recent batch run. `mode === "separate"`
   * (default) puts each builder on its own sheet; `"combined"` stacks
   * every table into a single sheet with the builder name as a title
   * row between them.
   * Disabled in the UI when no batch has run yet.
   */
  onExportBatch?: (mode: "separate" | "combined") => Promise<void> | void
  /** How many Builders currently have rows + columns (label on Run-all). */
  batchRunnableCount?: number
}

const FALLBACK_OP = TABLE_OPERATION_TYPE.XTAB
const EMPTY_VARS: string[] = []
const EMPTY_OPS: STATISTIC_TYPE[] = []

/**
 * Visual XTAB/DTAB builder with a tab strip for managing multiple
 * independent builder configurations. The active builder's state and last
 * result are read from useQueryBuilderStore so switching tabs preserves both
 * the configuration and the most recent run.
 */
const QueryBuilder: React.FC<QueryBuilderProps> = ({
  scriptInProgress,
  asyncScriptInProgress,
  splitSizes,
  onSplitChange,
  direction = "vertical",
  order = "console-first",
  onSaveActiveBuilder,
  onDeleteActiveBuilder,
  onRunBatch,
  onExportBatch,
  batchRunnableCount = 0,
}) => {
  const builder = useActiveBuilder()
  const result = useActiveBuilderResult()
  const { script, runnable } = useBuilderScript()

  const setOperation = useQueryBuilderStore((s) => s.setOperation)
  const addRow = useQueryBuilderStore((s) => s.addRow)
  const removeRow = useQueryBuilderStore((s) => s.removeRow)
  const addColumn = useQueryBuilderStore((s) => s.addColumn)
  const removeColumn = useQueryBuilderStore((s) => s.removeColumn)
  const toggleOperator = useQueryBuilderStore((s) => s.toggleOperator)
  const setUniverseFilter = useQueryBuilderStore((s) => s.setUniverseFilter)
  const setIsTransposed = useQueryBuilderStore((s) => s.setIsTransposed)
  const setSignificance = useQueryBuilderStore((s) => s.setSignificance)
  const setBuilderPipeline = useQueryBuilderStore(
    (s) => s.setBuilderTransformPipeline
  )
  const setBuilderOutputName = useQueryBuilderStore(
    (s) => s.setBuilderOutputName
  )
  const transformPipelines = useQueryBuilderStore(
    (s) => s.transformPipelines
  )
  const allBuilders = useQueryBuilderStore((s) => s.builders)

  const handleTransposeChange = useCallback(
    (checked: boolean) => setIsTransposed(checked),
    [setIsTransposed]
  )
  const handlePipelineChange = useCallback(
    (value: string | null) => setBuilderPipeline(value ?? null),
    [setBuilderPipeline]
  )

  // Defensive default — should not happen once syncToProject has run, but
  // keeps the component renderable during the initial mount tick.
  const operation = builder?.operation ?? FALLBACK_OP
  const rowVariables = builder?.rowVariables ?? EMPTY_VARS
  const columnVariables = builder?.columnVariables ?? EMPTY_VARS
  const selectedOperators = builder?.selectedOperators ?? EMPTY_OPS
  const universeFilter = builder?.universeFilter ?? null
  const isTransposed = builder?.isTransposed ?? false
  const transformPipelineId = builder?.transformPipelineId ?? null
  const significance = builder?.significance
  // While significance is on, the tested statistics are locked on (the
  // script emitter adds them to the operators).
  const lockedOperators = significance?.enabled
    ? SIGNIFICANCE_TESTED_STATISTICS[operation]
    : EMPTY_OPS
  const builderId = builder?.id
  const handleSignificanceChange = useCallback(
    (value: SignificanceSettings | undefined) => {
      if (builderId) setSignificance(builderId, value)
    },
    [builderId, setSignificance]
  )

  const builderSavedNames = useQueryBuilderStore((s) => s.builderSavedNames)
  const isBuilderSaved =
    !!builder && builderSavedNames[builder.id] === builder.name

  const { result: batchResult, builderIds: batchBuilderIds } =
    useBatchRunResult()
  const hasBatchResult = !!batchResult
  const batchOutputCount = batchResult?.result?.output?.length ?? 0
  const [isRunningBatch, setIsRunningBatch] = useState(false)
  // "active" shows the active-builder run (default + matches today's
  // behaviour). "batch" shows the multi-output result. Whenever a batch run
  // finishes, default the toggle to "batch" so the user sees the new data;
  // they can flip back to per-builder view at any time.
  const [outputView, setOutputView] = useState<"active" | "batch">("active")
  React.useEffect(() => {
    if (hasBatchResult) setOutputView("batch")
  }, [hasBatchResult, batchResult])
  const handleRunBatch = useCallback(async () => {
    if (!onRunBatch) return
    setIsRunningBatch(true)
    try {
      await onRunBatch()
    } finally {
      setIsRunningBatch(false)
    }
  }, [onRunBatch])
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportMode, setExportMode] = useState<"separate" | "combined">(
    "separate"
  )
  const [isExportingBatch, setIsExportingBatch] = useState(false)
  const openExportModal = useCallback(() => setExportModalOpen(true), [])
  const handleExportConfirm = useCallback(async () => {
    if (!onExportBatch) return
    setIsExportingBatch(true)
    try {
      await onExportBatch(exportMode)
      setExportModalOpen(false)
    } finally {
      setIsExportingBatch(false)
    }
  }, [onExportBatch, exportMode])

  // Validate the engine table id (a.k.a. outputName) against the same rules
  // the DATA(Q) tokenizer cares about plus uniqueness across the *other*
  // builders. The error is shown inline under the input; runs and saves are
  // still permitted because the engine will surface its own error if the
  // user pushes through.
  const outputName = builder?.outputName ?? ""
  const outputNameError = useMemo<string | null>(() => {
    if (!builder) return null
    const trimmed = outputName.trim()
    if (!trimmed) return "Required."
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed)) {
      return "Use letters, digits and underscore only (must start with a letter or underscore)."
    }
    const collision = allBuilders.find(
      (b) => b.id !== builder.id && b.outputName === trimmed
    )
    if (collision) {
      return `Already used by "${collision.name}".`
    }
    return null
  }, [allBuilders, builder, outputName])

  const handleOutputNameChange = useCallback(
    (value: string) => {
      if (!builder) return
      setBuilderOutputName(builder.id, value)
    },
    [builder, setBuilderOutputName]
  )
  const [isSavingBuilder, setIsSavingBuilder] = useState(false)
  const [saveAsModalOpen, setSaveAsModalOpen] = useState(false)

  const handleSaveBuilder = useCallback(async () => {
    if (!onSaveActiveBuilder || !builder) return
    setIsSavingBuilder(true)
    try {
      await onSaveActiveBuilder(builder.name)
    } finally {
      setIsSavingBuilder(false)
    }
  }, [builder, onSaveActiveBuilder])

  const handleSaveAsConfirm = useCallback(
    async (filename: string) => {
      if (!onSaveActiveBuilder || !filename.trim()) return
      setSaveAsModalOpen(false)
      setIsSavingBuilder(true)
      try {
        await onSaveActiveBuilder(filename.trim())
      } finally {
        setIsSavingBuilder(false)
      }
    },
    [onSaveActiveBuilder]
  )

  const NONE_VALUE = "__qcb_no_pipeline"
  const pipelineSelectValue =
    transformPipelineId &&
    transformPipelines.some((p) => p.id === transformPipelineId)
      ? transformPipelineId
      : NONE_VALUE
  const pipelineOptions = [
    { value: NONE_VALUE, label: "None (raw data)" },
    ...transformPipelines.map((p) => ({ value: p.id, label: p.name })),
  ]

  const builderControls = (
    <div className="qc-builder">
      <div className="qc-builder__toolbar">
        <BuilderOperationToggle value={operation} onChange={setOperation} />
        <Tooltip
          title="The QTable name this builder writes to in the engine. Must be a legal identifier (letters / digits / underscore, starts with a letter or underscore) and unique across builders. Used as the OUTPUT id."
          placement="bottom"
          overlayClassName="dataq-tooltip"
        >
          <label
            className="qc-builder__output-name"
            data-invalid={outputNameError ? "true" : "false"}
          >
            <span>Output:</span>
            <input
              type="text"
              className="qc-builder__output-name-input qc-mono"
              value={outputName}
              onChange={(e) => handleOutputNameChange(e.target.value)}
              spellCheck={false}
              aria-label="Output QTable name"
              aria-invalid={outputNameError ? "true" : "false"}
            />
            {outputNameError && (
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="qc-builder__output-name-warn"
                title={outputNameError}
              />
            )}
          </label>
        </Tooltip>
        <div className="qc-builder__toolbar-spacer" />
        <Tooltip
          title="Which Transform pipeline runs before this Builder. 'None' runs against raw data, so derived fields won't resolve."
          placement="bottom"
          overlayClassName="dataq-tooltip"
        >
          <label className="qc-builder__transpose">
            <span>Pipeline:</span>
            <Select
              size="small"
              value={pipelineSelectValue}
              onChange={(v) =>
                handlePipelineChange(v === NONE_VALUE ? null : v)
              }
              options={pipelineOptions}
              popupMatchSelectWidth={false}
              style={{ minWidth: 140 }}
            />
          </label>
        </Tooltip>
        <Tooltip
          title="Swap rows and columns in the output table"
          placement="bottom"
        >
          <label className="qc-builder__transpose">
            <Switch
              size="small"
              checked={isTransposed}
              onChange={handleTransposeChange}
            />
            <span>Transpose</span>
          </label>
        </Tooltip>
        {onRunBatch && (
          <Tooltip
            title="Runs every Builder with rows + columns in one engine call. Each Transform pipeline runs once even if multiple Builders share it."
            placement="bottom"
          >
            <span>
              <Button
                icon={<FontAwesomeIcon icon={faPlay} />}
                onClick={handleRunBatch}
                disabled={
                  batchRunnableCount === 0 ||
                  isRunningBatch ||
                  scriptInProgress
                }
              >
                Run all ({batchRunnableCount})
              </Button>
            </span>
          </Tooltip>
        )}
        {onExportBatch && (
          <>
            <Tooltip
              title={
                hasBatchResult
                  ? "Save every batch table to Excel — separate sheets or combined into one."
                  : "Run all first — then download every result as a single .xlsx."
              }
              placement="bottom"
            >
              <span>
                <Button
                  icon={<FontAwesomeIcon icon={faFileExcel} />}
                  onClick={openExportModal}
                  disabled={!hasBatchResult}
                  aria-label="Export all batch tables to Excel"
                >
                  Export all
                </Button>
              </span>
            </Tooltip>
            <ConsoleTableStyleButton variant="button" />
          </>
        )}
        {onSaveActiveBuilder && (
          <>
            <span className="qc-action-divider" aria-hidden />
            <Dropdown.Button
              size="small"
              type="primary"
              disabled={!builder || isSavingBuilder}
              loading={isSavingBuilder}
              onClick={handleSaveBuilder}
              menu={{
                items: [
                  {
                    key: "save-as",
                    label: "Save as new builder…",
                    onClick: () => setSaveAsModalOpen(true),
                  },
                ],
              }}
              buttonsRender={([leftButton, rightButton]) => [
                <Tooltip
                  key="left"
                  title={
                    isBuilderSaved
                      ? "Save (overwrite existing version)"
                      : "Save this builder to the project"
                  }
                  placement="bottom"
                >
                  {leftButton as React.ReactElement}
                </Tooltip>,
                rightButton,
              ]}
            >
              <FontAwesomeIcon icon={faFloppyDisk} />
              <span className="qc-transform__save-label">Save</span>
            </Dropdown.Button>
          </>
        )}
      </div>
      <ExportModal
        title="Save builder as"
        isVisible={saveAsModalOpen}
        confirm={(filename) => handleSaveAsConfirm(filename)}
        cancel={() => setSaveAsModalOpen(false)}
        message="Choose a name for the new builder. Existing saved builders with the same name will be overwritten."
        ExportButtonText="Save"
      />
      <Modal
        title="Export batch to Excel"
        open={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        onOk={handleExportConfirm}
        okText="Export"
        cancelText="Cancel"
        confirmLoading={isExportingBatch}
        destroyOnClose
      >
        <p style={{ marginTop: 0, color: "var(--qc-ink-muted)" }}>
          Choose how the {batchOutputCount || batchBuilderIds.length} table
          {(batchOutputCount || batchBuilderIds.length) === 1 ? "" : "s"} from
          the most recent batch run should be written to the workbook.
        </p>
        <Radio.Group
          value={exportMode}
          onChange={(e: RadioChangeEvent) => setExportMode(e.target.value)}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Radio value="separate">
              Each table on a separate sheet
            </Radio>
            <Radio value="combined">
              All tables on one sheet with titles
            </Radio>
          </div>
        </Radio.Group>
      </Modal>

      <div className="qc-builder__zones">
        <BuilderDropZone
          label="Rows"
          variables={rowVariables}
          onAdd={addRow}
          onRemove={removeRow}
        />
        <div className="qc-builder__zones-x-wrap" aria-hidden>
          <span className="qc-dropzone-label qc-builder__zones-x-spacer">
            &nbsp;
          </span>
          <span className="qc-builder__zones-x">×</span>
        </div>
        <BuilderDropZone
          label="Columns"
          variables={columnVariables}
          onAdd={addColumn}
          onRemove={removeColumn}
        />
      </div>

      <div className="qc-builder__section">
        <div className="qc-builder__section-label">Statistics</div>
        <BuilderStatsRow
          operation={operation}
          selectedOperators={selectedOperators}
          onToggle={toggleOperator}
          lockedOperators={lockedOperators}
        />
      </div>

      <div className="qc-builder__section">
        <div className="qc-builder__section-label">Significance</div>
        <SignificanceControl
          value={significance}
          onChange={handleSignificanceChange}
          tableType={operation}
        />
      </div>

      <div className="qc-builder__section">
        <div className="qc-builder__section-label">
          Conditions (filter)
          <Tooltip
            title={
              <>
                Filter the table to a specific group of respondents.
                <br />
                <br />
                Inside one group, a respondent passes if they match{" "}
                <strong>any</strong> chip.
                <br />
                <br />
                With more than one group, a respondent must match{" "}
                <strong>at least one chip in every group</strong>.
                <br />
                <br />
                Dropping a question adds each of its answer options as a chip
                you can keep or remove.
                <br />
                <br />
                Always produces <strong>one</strong> filtered table.
              </>
            }
            placement="top"
            overlayClassName="dataq-tooltip"
          >
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="qc-builder__section-info"
            />
          </Tooltip>
        </div>
        <BuilderConditions />
      </div>

      <div className="qc-builder__section">
        <div className="qc-builder__section-label">
          Universe (single value)
          <Tooltip
            title={
              <>
                A quick way to slice the table.
                <br />
                <br />
                A <strong>single answer option</strong> restricts the result
                to those respondents. You get <strong>one</strong> table.
                <br />
                <br />
                A <strong>whole question</strong> produces a separate table
                for each of its answer options.
              </>
            }
            placement="top"
            overlayClassName="dataq-tooltip"
          >
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="qc-builder__section-info"
            />
          </Tooltip>
        </div>
        <BuilderUniverseSelector
          value={universeFilter}
          onChange={setUniverseFilter}
        />
      </div>

      <BuilderQueryPreview script={script} isIncomplete={!runnable} />
    </div>
  )

  const shownResult =
    outputView === "batch" && batchResult ? batchResult : result
  const outputPane = (
    <div className="h-full overflow-hidden flex flex-col">
      {hasBatchResult && (
        <div className="qc-output__view-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={outputView === "active"}
            data-active={outputView === "active" ? "true" : "false"}
            onClick={() => setOutputView("active")}
          >
            Active
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={outputView === "batch"}
            data-active={outputView === "batch" ? "true" : "false"}
            onClick={() => setOutputView("batch")}
          >
            Batch ({batchOutputCount || batchBuilderIds.length})
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0">
        <Output
          result={shownResult}
          scriptInProgress={scriptInProgress}
          asyncScriptInProgress={asyncScriptInProgress}
        />
      </div>
    </div>
  )

  const builderPane = (
    <div className="qc-builder-scroll h-full">{builderControls}</div>
  )

  const containerClass =
    direction === "vertical"
      ? "split-container h-full flex flex-col"
      : "split-container h-full flex flex-row"

  const panes =
    order === "console-first"
      ? [builderPane, outputPane]
      : [outputPane, builderPane]

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <BuilderTabs onDeleteActiveBuilder={onDeleteActiveBuilder} />
      <Split
        key={`builder-${direction}-${order}`}
        direction={direction}
        sizes={splitSizes}
        onDragEnd={onSplitChange}
        minSize={120}
        expandToMin={false}
        gutterSize={6}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        className={`${containerClass} flex-1 min-h-0`}
      >
        {panes[0]}
        {panes[1]}
      </Split>
    </div>
  )
}

export default QueryBuilder
